import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  MotivoRechazo,
  MotivosRechazoPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMotivosRechazo = (
  params?: GetMotivosRechazoParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['motivo-rechazo', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getMotivosRechazo(params);
    },
    enabled,
    refetchOnWindowFocus: false
  });
};

export const useGetMotivoRechazo = (id: string) => {
  return useQuery({
    queryKey: ['motivo-rechazo', id],
    queryFn: async () => {
      return getMotivoRechazo(id);
    },
  });
};

export const useCreateMotivoRechazo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMotivoRechazo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['motivo-rechazo'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Motivo rechazo creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateMotivoRechazo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateMotivoRechazo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['motivo-rechazo'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Motivo Rechazo actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetMotivosRechazoParams = Partial<MotivoRechazo> & {
  page?: number;
  page_size?: number;

  // filters
  codigo_padre?: string;
  filterByState?: boolean;
};
export type CreateMotivoRechazoParams = Omit<
  MotivoRechazo,
  'id_motivorechazo_orden'
>;
export type UpdateMotivoRechazoParams = {
  id: string;
  data: CreateMotivoRechazoParams;
};

export const getMotivosRechazo = async (params?: GetMotivosRechazoParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<MotivosRechazoPagingRes>(
    `${urlBackend}api/v1/motivo-rechazo/?${queryParams}`,
    true
  );
};

export const getMotivoRechazo = async (id: string) => {
  try {
    return await get<MotivoRechazo>(
      `${urlBackend}api/v1/motivo-rechazo/${id}`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createMotivoRechazo = async (data: CreateMotivoRechazoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<MotivoRechazo>(`${urlBackend}api/v1/motivo-rechazo/`, data, true);
};

export const updateMotivoRechazo = async ({
  id,
  data,
}: UpdateMotivoRechazoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<MotivoRechazo>(
    `${urlBackend}api/v1/motivo-rechazo/${id}/`,
    data,
    true
  );
};
